/**
 * RubricViewV2 - Modern Rubric Grading Interface
 *
 * V2 implementation of the Rubric grading component with:
 * - Modern card-based design for criteria
 * - Radio button selection for ratings
 * - Run/View dropdowns for test cases
 * - Student navigation with prev/next arrows
 * - Integrated comment system
 */
import React, { useEffect, useReducer, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Dropdown, OverlayTrigger, Tooltip, Spinner, Form } from 'react-bootstrap'
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiMessageSquare } from 'react-icons/fi'
import { VscRunAll } from 'react-icons/vsc'
import { AiOutlineEye } from 'react-icons/ai'
import * as constants from 'Constants'
import httpService from 'services/httpService'
import displayGlobalMessage from 'Utils/DisplayGlobalMessage'
import StudentSearchableDropdown from '../../../Programming/Components/SubjectiveGrading/SearchableDropDown'
import ManualMarksTemplate from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/ManualMarksTemplate'
import TestCaseModal from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/TestCaseModal'
import Multicomment from '../../../Programming/Components/RubricView/Multicomment'
import RunTerminal from '../../../Programming/ComponentsNew/ManageSubmissions/RunTerminal'
import styles from './RubricViewV2.module.css'

const RubricViewV2 = ({
  programId,
  submission,
  setSubmissionPath,
  setSubmission,
  submissionData,
  setSubmissionData,
  isStudentView = false,
  nextSubmissionURL = null,
  setNextSubmissionURL = () => {},
  numOfRows = 100,
}) => {
  // State management
  const [gradingHistory, setGradingHistory] = useState({})
  const [aiGradingHistory, setAiGradingHistory] = useState({})
  const [totalScore, setTotalScore] = useState(0)
  const [rubricData, setRubricData] = useState([])
  const [isGraded, setIsGraded] = useState(false)
  const [showCommentBox, setShowCommentBox] = useState({})
  const [loading, setLoading] = useState(true)
  const [showRubrics, setShowRubrics] = useState(true)

  // Test case modal state
  const [showTestCaseModal, setShowTestCaseModal] = useState(false)
  const [testcaseDetails, setTestcaseDetails] = useState(null)
  const [runningTestcaseType, setRunningTestcaseType] = useState(null)

  // Custom run terminal state
  const [showCustomTerminal, setShowCustomTerminal] = useState(false)
  const [runToggle, setRunToggle] = useState(false)
  const [errorOnRun, setErrorOnRun] = useState(null)
  const [successOnRun, setSuccessOnRun] = useState(false)
  const [isRunLoading, setIsRunLoading] = useState(false)

  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  const location = useLocation()

  // Fetch rubric data (only when programId changes)
  useEffect(() => {
    const fetchRubricData = async () => {
      if (programId) {
        const url = `${constants.base_url}/evalpro/rubric/read_rubric/${programId}`

        try {
          const response = await httpService.get(url)
          const array = []
          response.data.forEach((criteria) => {
            array.push({
              criteria_id: criteria.id,
              name: criteria.title,
              description: criteria.description,
              isModelAvail: criteria.is_ai_grading_available,
              Ratings: criteria.Ratings.map((rating) => ({
                rating_id: rating.id,
                tag: rating.title,
                description: rating.description,
                mark: rating.marks,
              })).sort((a, b) => (a.mark > b.mark ? -1 : 1)),
            })
          })

          setRubricData(array)

          const maxScores = array.map((criteria) =>
            Math.max(...criteria.Ratings.map((ratings) => ratings.mark))
          )
          setTotalScore(maxScores.reduce((a, b) => a + parseFloat(b), 0))
        } catch (error) {
          console.error(error)
          displayGlobalMessage('Reading rubric data failed', 'error')
        }
      }
    }

    fetchRubricData()
  }, [programId])

  // Fetch AI grades (when submission changes)
  useEffect(() => {
    const fetchAiGrades = async () => {
      if (submission?.id) {
        try {
          const aiResponse = await httpService.get(
            `${constants.base_url}/evalpro/rubric/read_ai_grades/${submission.id}`
          )
          const aiHistory = {}
          aiResponse.data.forEach((history) => {
            aiHistory[history.criteria] = {
              mark: history.marks,
              id: history.rating,
              comments: history.comments,
            }
          })
          setAiGradingHistory(aiHistory)
          setLoading(false)
        } catch (error) {
          console.error(error)
          setLoading(false)
          // AI grades might not exist for all submissions, so don't show error
        }
      }
    }

    fetchAiGrades()
  }, [submission?.id])

  // Fetch grading history
  useEffect(() => {
    const fetchGradingHistory = async () => {
      if (submission && rubricData.length > 0) {
        let currentHistory = {}
        let newShowCommentBox = {} // Reset comment box state for new student
        const url = `${constants.base_url}/evalpro/rubric/read_grades/${submission.id}`

        try {
          const response = await httpService.get(url)

          response.data.forEach((history) => {
            if (history.is_active) {
              currentHistory[history.criteria] = {
                mark: history.marks,
                id: history.rating,
                is_graded_by_ai: history.is_graded_by_ai,
                comments: history.comments,
              }
            }

            if (history.comments !== '') {
              newShowCommentBox[history.criteria] = true
            }
          })

          setShowCommentBox(newShowCommentBox)
          setGradingHistory(currentHistory)

          const isAnyIdMinusOne = Object.values(currentHistory).some(
            (history) => history.id === -1
          )

          if (isAnyIdMinusOne || submission.graded_by === 'Not Graded') {
            setIsGraded(false)
          } else {
            setIsGraded(rubricData.length === Object.keys(currentHistory).length)
          }
        } catch (error) {
          console.error(error)
          displayGlobalMessage('Error in reading student grades.', 'error')
        }
      }
    }
    fetchGradingHistory()
  }, [submission, rubricData])

  // Fetch dropdown data for pagination
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!isStudentView && nextSubmissionURL !== null) {
        const response = await httpService.get(nextSubmissionURL)
        const loadedData = response.data.submissions.submissions.results
        const _oldSubmissionData = [...submissionData]
        const parsedUrl = new URL(nextSubmissionURL)
        const pageNumber = parsedUrl.searchParams.get('page')
        let lastPageSize = numOfRows
        if (loadedData.length < numOfRows) {
          lastPageSize = loadedData.length
        }
        Array.prototype.splice.apply(_oldSubmissionData, [
          (pageNumber - 1) * numOfRows,
          lastPageSize,
          ...loadedData,
        ])
        setNextSubmissionURL(response.data.submissions.submissions.next)
        setSubmissionData(_oldSubmissionData)
      }
    }
    fetchDropdownData()
  }, [nextSubmissionURL])

  // Update manual marks
  const updateSubmission = async (rowData, id, manual_marks) => {
    const studentId = rowData?.student
    const url = `${constants.base_url}/evalpro/manual_marks/${id}`
    const form_data = new FormData()
    form_data.append('obtained_manual_marks', manual_marks)
    form_data.append('student_id', studentId)

    try {
      await httpService.put(url, form_data)
      forceUpdate()
    } catch (error) {
      console.error(error)
      displayGlobalMessage('Error Occurred while Saving Manual Marks', 'error')
    }
  }

  const updateTotalMarks = (totalMarks) => {
    const rowData = location.state.submission
    rowData.total_marks = totalMarks
  }

  // Toggle comment box
  const toggleCommentBox = (criteriaId) => {
    setShowCommentBox({ ...showCommentBox, [criteriaId]: !showCommentBox[criteriaId] })
  }

  // Calculate max marks for criterion
  const getMarksDisplay = (ratings, row_index) => {
    let max = Math.max(...ratings.map((r) => parseFloat(r.mark)))
    let score = 0
    if (gradingHistory.hasOwnProperty(rubricData[row_index].criteria_id)) {
      score = parseFloat(gradingHistory[rubricData[row_index].criteria_id].mark)
    }
    return { score, max }
  }

  // Calculate total marks given
  const calcMarksGiven = () => {
    const givenScores = Object.keys(gradingHistory).map(
      (key) => gradingHistory[key].mark
    )
    return givenScores.reduce((a, b) => a + parseFloat(b), 0)
  }

  // Handle comment update
  const handleUpdateCriterionComment = (e, row_index) => {
    let currentHistory = { ...gradingHistory }

    if (currentHistory.hasOwnProperty(rubricData[row_index].criteria_id)) {
      currentHistory[rubricData[row_index].criteria_id].comments = e.target.value
    } else {
      currentHistory[rubricData[row_index].criteria_id] = {
        mark: 0,
        id: -1,
        is_graded_by_ai: false,
        comments: e.target.value,
      }
    }
    setGradingHistory(currentHistory)
  }

  // Handle rating selection
  const handleSelectRating = (row_index, col_index) => {
    let currentHistory = { ...gradingHistory }
    const selected_id = rubricData[row_index].Ratings[col_index].rating_id
    const selected_mark = rubricData[row_index].Ratings[col_index].mark

    if (
      currentHistory.hasOwnProperty(rubricData[row_index].criteria_id) &&
      selected_id === currentHistory[rubricData[row_index].criteria_id].id
    ) {
      // Unselect
      currentHistory[rubricData[row_index].criteria_id] = {
        ...currentHistory[rubricData[row_index].criteria_id],
        mark: 0,
        id: -1,
        is_graded_by_ai: false,
      }
    } else {
      // Select
      currentHistory[rubricData[row_index].criteria_id] = {
        ...(currentHistory[rubricData[row_index].criteria_id]?.comments
          ? {}
          : { comments: '' }),
        ...currentHistory[rubricData[row_index].criteria_id],
        mark: selected_mark,
        id: selected_id,
        is_graded_by_ai: false,
      }
    }
    setGradingHistory(currentHistory)
  }

  // Handle submit
  const handleSubmit = async () => {
    const id = submission.id
    let form_data = new FormData()
    const givenScores = Object.keys(gradingHistory).map(
      (key) => gradingHistory[key].mark
    )

    if (givenScores.length === 0) {
      displayGlobalMessage('Please select at least 1 Rating for Saving Grades', 'error')
      return
    }

    // Check if any criteria is left ungraded
    const ungradedCriteria = rubricData.filter((criterion) => {
      const history = gradingHistory[criterion.criteria_id]
      return !history || history.id === -1
    })

    if (ungradedCriteria.length > 0) {
      const ungradedNames = ungradedCriteria.map((c) => {
        const criterionIndex = rubricData.findIndex((r) => r.criteria_id === c.criteria_id)
        return `Criterion ${criterionIndex + 1}`
      }).join(', ')
      displayGlobalMessage(
        `Please grade all criteria before submitting. Ungraded: ${ungradedNames}`,
        'error'
      )
      return
    }

    const sum = givenScores.reduce((a, b) => a + parseFloat(b), 0)

    try {
      const studentId = submission?.student
      const url = `${constants.base_url}/evalpro/rubric_marks/${id}/`
      form_data.append('obtained_rubric_marks', sum)
      form_data.append('student_id', studentId)
      await httpService.put(url, form_data)
      displayGlobalMessage('Marks added successfully', 'success')
    } catch (error) {
      console.error(error)
      displayGlobalMessage('Error Occurred while Saving Rubric Marks', 'error')
    }

    try {
      const url2 = `${constants.base_url}/evalpro/rubric/add_grades/${id}/`
      await httpService.post(url2, gradingHistory)
      displayGlobalMessage('Rubric grades saved successfully', 'success')

      const isAnyIdMinusOne = Object.values(gradingHistory).some(
        (history) => history.id === -1
      )
      if (isAnyIdMinusOne || submission.graded_by === 'Not Graded') {
        setIsGraded(false)
      } else {
        setIsGraded(rubricData.length === Object.keys(gradingHistory).length)
      }
    } catch (error) {
      displayGlobalMessage('Server error', 'error')
    }
  }

  // Handle reset
  const handleResetRubrics = () => {
    setGradingHistory({})
  }

  // Run test cases
  const runTestCaseButton = async (testcaseType) => {
    setRunningTestcaseType(testcaseType)
    const url = `${constants.base_url}/evalpro/evaluate/${testcaseType}/${submission.id}`
    const form_data = new FormData()
    form_data.append('student_id', submission?.student)

    try {
      const response = await httpService.put(url, form_data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      displayGlobalMessage(
        `${response.data.passed_testcases_count}/${response.data.testcase_details.length} ${testcaseType} testcases passed`,
        'success'
      )

      setTestcaseDetails({
        submissionID: submission?.id,
        type: testcaseType === 'visible' ? 'visible_testcases' : 'hidden_testcases',
        studentName: submission?.first_name,
        studentEmail: submission?.email,
        obtainedMarks:
          testcaseType === 'visible'
            ? submission?.obtained_visible_marks
            : submission?.obtained_hidden_marks,
        studentID: submission?.student,
      })
      setShowTestCaseModal(true)
      setRunningTestcaseType(null)
    } catch (error) {
      setRunningTestcaseType(null)
      console.error(error)
      displayGlobalMessage('Error running test cases', 'error')
    }
  }

  // Handle next/previous submission
  const handleClick = (type) => {
    // Get submission data from props or localStorage
    let data = submissionData

    if (!data || data.length === 0) {
      const stored = localStorage.getItem('submissionData')
      if (stored) {
        try {
          data = JSON.parse(stored)
          setSubmissionData(data)
        } catch (e) {
          console.error('Error parsing submissionData from localStorage:', e)
          return
        }
      }
    }

    if (!data || data.length === 0) {
      return
    }

    const currentSubmissionId = submission?.id
    const len = data.length

    // Find current index
    let currentIndex = -1
    for (let i = 0; i < len; i++) {
      if (data[i]?.id === currentSubmissionId) {
        currentIndex = i
        break
      }
    }

    if (currentIndex === -1) {
      return
    }

    // Calculate target index with wrap-around
    let targetIndex
    if (type === 'next') {
      targetIndex = currentIndex + 1 >= len ? 0 : currentIndex + 1
    } else {
      targetIndex = currentIndex - 1 < 0 ? len - 1 : currentIndex - 1
    }

    // Find next valid submission (skip "Not Uploaded" submissions)
    const startIndex = targetIndex
    let found = false
    do {
      const targetSubmission = data[targetIndex]
      if (targetSubmission && targetSubmission.submission_status !== 'Not Uploaded') {
        setSubmissionPath(targetSubmission.file_submitted)
        setSubmission(targetSubmission)
        found = true
        break
      }
      // Move to next/previous
      if (type === 'next') {
        targetIndex = targetIndex + 1 >= len ? 0 : targetIndex + 1
      } else {
        targetIndex = targetIndex - 1 < 0 ? len - 1 : targetIndex - 1
      }
    } while (targetIndex !== startIndex)

  }

  // Handle custom input run
  const handleRunWithCustomInput = async () => {
    setIsRunLoading(true)
    setErrorOnRun(null)
    setSuccessOnRun(false)

    const url = `${constants.base_url}/evalpro/editor/check_compilation_status/${programId}`
    const form_data = new FormData()
    form_data.append('programming_language', submission.submission_program_language)
    form_data.append('key', 'ta_buffer_data')
    form_data.append('Student_Email', submission.email)

    try {
      const response = await httpService.put(url, form_data)
      if (response.data.result.status === 'success') {
        setSuccessOnRun(true)
        setRunToggle(true)
        setShowCustomTerminal(true)
      } else {
        setErrorOnRun(response.data.result.error || 'Compilation failed')
        setRunToggle(true)
        setShowCustomTerminal(true)
      }
    } catch (error) {
      console.error(error)
      setErrorOnRun('Failed to check compilation status')
      displayGlobalMessage('Error checking compilation', 'error')
    } finally {
      setIsRunLoading(false)
    }
  }

  // Render grading status badge
  const renderStatusBadge = () => {
    if (rubricData.length === 0) {
      return <span className={`${styles.statusBadge} ${styles.statusNoRubrics}`}>No Rubrics</span>
    }
    if (isGraded) {
      return <span className={`${styles.statusBadge} ${styles.statusGraded}`}>Graded</span>
    }
    return <span className={`${styles.statusBadge} ${styles.statusUngraded}`}>Ungraded</span>
  }

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Student Selector */}
      {!isStudentView && (
        <div className={styles.studentSelectorSection}>
          <div className={styles.studentSelectorRow}>
            <OverlayTrigger overlay={<Tooltip>Previous Submission</Tooltip>}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => handleClick('previous')}
              >
                <FiChevronLeft size={20} />
              </button>
            </OverlayTrigger>

            <div className={styles.studentDropdownWrapper}>
              <StudentSearchableDropdown
                submissionData={submissionData}
                setSubmissionData={setSubmissionData}
                setSubmissionPath={setSubmissionPath}
                setSubmission={setSubmission}
                submission={submission}
                programId={programId}
              />
            </div>

            <OverlayTrigger overlay={<Tooltip>Next Submission</Tooltip>}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => handleClick('next')}
              >
                <FiChevronRight size={20} />
              </button>
            </OverlayTrigger>
          </div>

          <div className={styles.statusBadgeRow}>
            {renderStatusBadge()}
          </div>
        </div>
      )}

      {/* Rubric Content */}
      {showRubrics ? (
        <div className={styles.rubricContent}>
          {/* Criteria Cards */}
          {rubricData.map((criterion, row_index) => {
            const { score, max } = getMarksDisplay(criterion.Ratings, row_index)

            return (
              <div key={criterion.criteria_id} className={styles.criterionCard}>
                <div className={styles.criterionHeader}>
                  <h3 className={styles.criterionTitle}>Criterion {row_index + 1}</h3>
                  <p className={styles.criterionDescription}>
                    {criterion.name}: {criterion.description}
                  </p>
                  <div className={styles.criterionMarks}>
                    Marks: <b>{score}</b>/<b>{max}</b>
                  </div>
                </div>

                <div className={styles.ratingSection}>
                  <div className={styles.ratingLabel}>RATING</div>
                  <div className={styles.ratingOptions}>
                    {criterion.Ratings.map((rating, col_index) => {
                      const isSelected =
                        gradingHistory.hasOwnProperty(criterion.criteria_id) &&
                        gradingHistory[criterion.criteria_id].id === rating.rating_id
                      const isAiSuggested =
                        aiGradingHistory.hasOwnProperty(criterion.criteria_id) &&
                        aiGradingHistory[criterion.criteria_id].id === rating.rating_id

                      return (
                        <div
                          key={rating.rating_id}
                          className={`${styles.ratingOption} ${
                            isSelected ? styles.ratingOptionSelected : ''
                          } ${isAiSuggested && !isSelected ? styles.ratingOptionAiSuggested : ''}`}
                          onClick={
                            !isStudentView
                              ? () => handleSelectRating(row_index, col_index)
                              : undefined
                          }
                        >
                          <Form.Check
                            type="radio"
                            className={styles.ratingRadio}
                            checked={isSelected}
                            onChange={() => {}}
                            disabled={isStudentView}
                          />
                          <span className={styles.ratingTag}>{rating.tag}</span>
                          <div className={styles.ratingContent}>
                            <span className={styles.ratingDescription}>
                              {rating.description}
                            </span>
                          </div>
                          <span className={styles.ratingMark}>{rating.mark}</span>
                          {!isStudentView && (
                            <OverlayTrigger overlay={<Tooltip>Toggle Comments</Tooltip>}>
                              <FiMessageSquare
                                size={18}
                                className={`${styles.commentIcon} ${
                                  showCommentBox[criterion.criteria_id]
                                    ? styles.commentIconActive
                                    : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleCommentBox(criterion.criteria_id)
                                }}
                              />
                            </OverlayTrigger>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Comment Box */}
                {showCommentBox[criterion.criteria_id] && (
                  <div className={styles.commentBox}>
                    <div className={styles.commentColumn}>
                      <span className={styles.commentColumnLabel}>TA Buddy Comment:</span>
                      <div className={styles.aiCommentBox}>
                        {aiGradingHistory[criterion.criteria_id]?.comments ||
                          'No comments provided.'}
                      </div>
                    </div>
                    <div className={styles.commentColumn}>
                      <span className={styles.commentColumnLabel}>Your Comment:</span>
                      <textarea
                        className={styles.commentTextarea}
                        placeholder={isStudentView ? 'View only' : 'Add your comment...'}
                        value={
                          gradingHistory[criterion.criteria_id]?.comments || ''
                        }
                        onChange={(e) => handleUpdateCriterionComment(e, row_index)}
                        disabled={isStudentView}
                        readOnly={isStudentView}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}

          {/* Marks Summary */}
          <div className={styles.marksSummary}>
            {rubricData.length === 0
              ? <>Rubrics Marks: <b>Not Applicable</b></>
              : <>Rubrics Marks: <b>{calcMarksGiven()}</b> out of <b>{totalScore}</b></>
            }
          </div>

          {/* Action Buttons */}
          {rubricData.length > 0 && (
            <div className={styles.actionButtons}>
              {!isStudentView && (
                <button className={styles.submitBtn} onClick={handleSubmit}>
                  Submit
                </button>
              )}
              <button
                className={styles.hideBtn}
                onClick={() => setShowRubrics(false)}
              >
                Hide Rubrics
              </button>
              {!isStudentView && (
                <button className={styles.resetBtn} onClick={handleResetRubrics}>
                  Reset
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.showRubricsSection}>
          <div className={styles.marksSummary}>
            {rubricData.length === 0
              ? <>Rubrics Marks: <b>Not Applicable</b></>
              : <>Rubrics Marks: <b>{calcMarksGiven()}</b> out of <b>{totalScore}</b></>
            }
          </div>
          {rubricData.length > 0 && (
            <button
              className={styles.showRubricsBtn}
              onClick={() => setShowRubrics(true)}
            >
              Show Rubrics
            </button>
          )}
        </div>
      )}

      {/* Marks Section */}
      <div className={styles.marksSection}>
        <div className={styles.marksItem}>
          Evaluation Test Case Marks:{' '}
          <b>
            {submission.obtained_hidden_marks} / {submission.total_hidden_testcase_marks}
          </b>
        </div>
        <div className={styles.marksItem}>
          Manual Marks: {isStudentView && <b>{submission.obtained_manual_marks}</b>}
          {!isStudentView && (
            <ManualMarksTemplate
              rowData={submission}
              updateSubmission={updateSubmission}
              updateTotalMarks={updateTotalMarks}
              setSubmissionData={setSubmissionData}
            />
          )}
        </div>
      </div>

      {/* Run/View Dropdowns */}
      <div className={styles.dropdownsSection}>
        {/* Run Dropdown */}
        <Dropdown>
          <Dropdown.Toggle as="button" className={styles.runDropdown}>
            <VscRunAll size={18} />
            Run
            <FiChevronDown size={16} />
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.dropdownMenu}>
            <Dropdown.Item
              className={styles.dropdownItem}
              onClick={() => runTestCaseButton('visible')}
              disabled={runningTestcaseType !== null}
            >
              {runningTestcaseType === 'visible' ? 'Running...' : 'Run Practice Test Cases'}
            </Dropdown.Item>
            <Dropdown.Item
              className={styles.dropdownItem}
              onClick={() => runTestCaseButton('hidden')}
              disabled={runningTestcaseType !== null}
            >
              {runningTestcaseType === 'hidden' ? 'Running...' : 'Run Evaluation Test Cases'}
            </Dropdown.Item>
            {!isStudentView && (
              <Dropdown.Item
                className={styles.dropdownItem}
                onClick={handleRunWithCustomInput}
                disabled={isRunLoading}
              >
                {isRunLoading ? 'Checking Compilation...' : 'Run with Custom Input'}
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>

        {/* View Dropdown */}
        <Dropdown>
          <Dropdown.Toggle as="button" className={styles.viewDropdown}>
            <AiOutlineEye size={18} />
            View
            <FiChevronDown size={16} />
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.dropdownMenu}>
            <Dropdown.Item
              className={styles.dropdownItem}
              onClick={() => {
                setTestcaseDetails({
                  submissionID: submission?.id,
                  type: 'visible_testcases',
                  studentName: submission?.first_name,
                  studentEmail: submission?.email,
                  obtainedMarks: submission?.obtained_visible_marks,
                  studentID: submission?.student,
                })
                setShowTestCaseModal(true)
              }}
            >
              View Visible Testcase Details
            </Dropdown.Item>
            <Dropdown.Item
              className={styles.dropdownItem}
              onClick={() => {
                setTestcaseDetails({
                  submissionID: submission?.id,
                  type: 'hidden_testcases',
                  studentName: submission?.first_name,
                  studentEmail: submission?.email,
                  obtainedMarks: submission?.obtained_hidden_marks,
                  studentID: submission?.student,
                })
                setShowTestCaseModal(true)
              }}
            >
              View Hidden Testcase Details
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Test Case Modal */}
      <TestCaseModal
        key={testcaseDetails?.type}
        submissionID={testcaseDetails?.submissionID}
        testcaseType={testcaseDetails?.type}
        showModal={showTestCaseModal}
        setShowModal={setShowTestCaseModal}
        studentName={testcaseDetails?.studentName}
        studentEmail={testcaseDetails?.studentEmail}
        obtainedMarks={testcaseDetails?.obtainedMarks}
        studentID={testcaseDetails?.studentID}
      />

      {/* Custom Terminal */}
      {!isStudentView && showCustomTerminal && (
        <div className={styles.terminalSection}>
          <RunTerminal
            programId={programId}
            language={submission.submission_program_language}
            submission={submission}
            errorOnRun={errorOnRun}
            successOnRun={successOnRun}
            runToggle={runToggle}
            setRunToggle={setRunToggle}
            setErrorOnRun={setErrorOnRun}
            setSuccessOnRun={setSuccessOnRun}
            isRunLoading={isRunLoading}
            setShowCustomTerminal={setShowCustomTerminal}
          />
        </div>
      )}

      {/* Multicomment Section */}
      <div className={styles.commentSection}>
        <Multicomment submission={submission} isStudentView={isStudentView} />
      </div>
    </div>
  )
}

export default RubricViewV2
